import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  CalendarDays,
  Plus,
  Trash2,
  ChevronRight,
  RefreshCw,
  Tag,
  CheckCircle2,
  CircleDot,
  XCircle,
} from "lucide-react";
import api from "../api/api";
import { useAuth } from "../auth/AuthProvider";
import { useDialog } from "../components/DialogProvider";

const STATUS_MAP = {
  draft:  { label: "Draft",  tone: "slate",   icon: CircleDot },
  active: { label: "Aktif",  tone: "emerald",  icon: CheckCircle2 },
  closed: { label: "Selesai", tone: "rose",    icon: XCircle },
};

function Events() {
  const auth = useAuth();
  const isAdmin = auth.user?.role === "admin";
  const dialog = useDialog();
  const [events, setEvents]     = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [showForm, setShowForm] = useState(false);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const res = await api.get("/events");
      setEvents(res.data || []);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.message || "Gagal mengambil data event");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEvents(); }, []);

  const handleDelete = async (id, name) => {
    const ok = await dialog.confirm(`Hapus event "${name}"? Aksi ini tidak bisa dibatalkan.`, { title: "Hapus Event" });
    if (!ok) return;
    try {
      await api.delete(`/events/${id}`);
      setEvents((prev) => prev.filter((e) => e.id !== id));
    } catch (e) {
      await dialog.alert(e?.response?.data?.message || "Gagal menghapus event", { title: "Error" });
    }
  };

  return (
    <div className="space-y-6 animate-fade-in pb-10 max-w-[1400px]">
      {/* Header */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-zinc-600 shadow-sm">
            <CalendarDays className="h-3 w-3 text-sky-600" />
            POS Event
          </div>
          <h1 className="mt-3 text-2xl font-semibold tracking-tight text-zinc-900">
            Daftar Event
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            Kelola event penjualan — buat event, transfer stok dari gudang, dan atur operasional kasir.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchEvents} disabled={loading} className="inline-flex items-center justify-center gap-2 rounded-md border border-zinc-200 bg-white px-4 py-2 text-[13px] font-medium text-zinc-700 shadow-sm hover:bg-zinc-50 transition-colors focus:outline-none focus:ring-1 focus:ring-zinc-900">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          {isAdmin && (
            <button onClick={() => setShowForm(true)} className="inline-flex items-center justify-center gap-2 rounded-md bg-zinc-900 px-4 py-2 text-[13px] font-medium text-white shadow-sm hover:bg-zinc-800 transition-colors focus:outline-none focus:ring-1 focus:ring-zinc-900">
              <Plus className="h-4 w-4" />
              Buat Event
            </button>
          )}
        </div>
      </header>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-medium text-rose-700">
          {error}
        </div>
      )}

      {/* Create Form Modal */}
      {showForm && (
        <CreateEventModal
          onClose={() => setShowForm(false)}
          onCreated={(newEvent) => {
            setEvents((prev) => [newEvent, ...prev]);
            setShowForm(false);
          }}
        />
      )}

      {/* Event List */}
      {loading && events.length === 0 ? (
        <div className="flex items-center justify-center p-12 text-sm text-zinc-500">
          <RefreshCw className="h-5 w-5 animate-spin mr-2" /> Memuat data event...
        </div>
      ) : events.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50 p-12 text-center">
          <h3 className="text-sm font-semibold text-zinc-900">Belum ada event</h3>
          <p className="mt-1 text-[13px] text-zinc-500 max-w-md mx-auto">
            Buat event pertama Anda untuk mulai memindahkan stok dari gudang dan melakukan penjualan lewat kasir mandiri.
          </p>
          {isAdmin && (
            <button onClick={() => setShowForm(true)} className="mt-4 inline-flex items-center justify-center gap-2 rounded-md bg-zinc-900 px-4 py-2 text-[13px] font-medium text-white shadow-sm hover:bg-zinc-800 transition-colors">
              <Plus className="h-4 w-4" /> Buat Event Sekarang
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {events.map((ev) => (
            <EventCard key={ev.id} event={ev} onDelete={handleDelete} isAdmin={isAdmin} />
          ))}
        </div>
      )}
    </div>
  );
}

function EventCard({ event, onDelete, isAdmin }) {
  const status = STATUS_MAP[event.status] || STATUS_MAP.draft;
  const StatusIcon = status.icon;

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm hover:border-zinc-300 transition-colors">
      <div className="flex items-start justify-between gap-3 p-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ring-1 ring-inset ${
              status.tone === 'emerald' ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' :
              status.tone === 'rose' ? 'bg-rose-50 text-rose-700 ring-rose-600/20' :
              'bg-zinc-50 text-zinc-700 ring-zinc-500/20'
            }`}>
              <StatusIcon className="h-3 w-3" />
              {status.label}
            </span>
          </div>
          <h3 className="mt-3 truncate text-[15px] font-semibold text-zinc-900" title={event.name}>
            {event.name}
          </h3>
          <div className="mt-1 flex items-center gap-1.5 text-[11px] font-medium text-zinc-500">
            <CalendarDays className="h-3.5 w-3.5" />
            {event.start_date} — {event.end_date}
          </div>
        </div>
        {event.status !== "active" && isAdmin && (
          <button
            onClick={() => onDelete(event.id, event.name)}
            className="shrink-0 rounded-md p-1.5 text-zinc-400 hover:bg-rose-50 hover:text-rose-600 transition-colors focus:outline-none"
            title="Hapus event"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-3 divide-x divide-zinc-100 border-t border-zinc-100 bg-zinc-50/50">
        <StatCell label="Total Produk" value={event.total_products ?? 0} />
        <StatCell label="Sesi Kasir" value={event.total_sessions ?? 0} />
        <StatCell label="Total Omset" value={fmtCurrency(event.total_revenue ?? 0)} small />
      </div>

      <div className="border-t border-zinc-100 p-4">
        <Link
          to={`/events/${event.id}`}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 text-[13px] font-medium text-zinc-700 shadow-sm hover:bg-zinc-50 transition-colors"
        >
          <Tag className="h-4 w-4" />
          Kelola Event
          <ChevronRight className="h-4 w-4 ml-auto text-zinc-400" />
        </Link>
      </div>
    </div>
  );
}

function StatCell({ label, value, small }) {
  return (
    <div className="px-3 py-3 text-center">
      <div className={`font-semibold text-zinc-900 tabular-nums tracking-tight ${small ? "text-sm" : "text-base"}`}>
        {value}
      </div>
      <div className="mt-0.5 text-[10px] font-medium uppercase tracking-wider text-zinc-500">{label}</div>
    </div>
  );
}

function CreateEventModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", start_date: "", end_date: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.name || !form.start_date || !form.end_date) {
      setError("Semua field wajib diisi");
      return;
    }
    if (form.start_date > form.end_date) {
      setError("Tanggal mulai tidak boleh lebih dari tanggal selesai");
      return;
    }
    try {
      setLoading(true);
      const res = await api.post("/events", form);
      onCreated(res.data?.event || res.data);
    } catch (e) {
      setError(e?.response?.data?.message || "Gagal membuat event");
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-zinc-950/50 backdrop-blur-sm p-4 animate-fade-in">
      <div className="w-full max-w-md rounded-lg border border-zinc-200 bg-white shadow-xl animate-fade-in overflow-hidden">
        <div className="flex items-center justify-between border-b border-zinc-100 bg-zinc-50/50 px-5 py-4">
          <h2 className="text-[15px] font-bold text-zinc-900">Buat Event Baru</h2>
          <button onClick={onClose} className="rounded-md p-1.5 hover:bg-zinc-100 text-zinc-500 transition-colors">
            <XCircle className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-5 p-5">
          {error && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] font-medium text-rose-700">
              {error}
            </div>
          )}
          <Field label="Nama Event">
            <input
              className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-[13px] outline-none transition-all focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 shadow-sm"
              placeholder="contoh: Pameran Jakarta 2025"
              value={form.name}
              onChange={set("name")}
              autoFocus
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Tanggal Mulai">
              <input className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-[13px] outline-none transition-all focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 shadow-sm" type="date" value={form.start_date} onChange={set("start_date")} />
            </Field>
            <Field label="Tanggal Selesai">
              <input className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-[13px] outline-none transition-all focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 shadow-sm" type="date" value={form.end_date} onChange={set("end_date")} />
            </Field>
          </div>
        </div>
        <div className="flex gap-3 border-t border-zinc-100 bg-zinc-50/50 px-5 py-4">
          <button onClick={onClose} className="flex-1 rounded-md border border-zinc-200 bg-white py-2 text-[13px] font-bold text-zinc-700 shadow-sm hover:bg-zinc-50 transition-colors">Batal</button>
          <button onClick={handleSubmit} disabled={loading} className="flex-1 rounded-md bg-zinc-900 py-2 text-[13px] font-bold text-white shadow-sm hover:bg-zinc-800 transition-colors disabled:opacity-50">
            {loading ? "Menyimpan..." : "Buat Event"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-widest text-zinc-500">
        {label}
      </label>
      {children}
    </div>
  );
}

function fmtCurrency(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}jt`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(0)}rb`;
  return String(v);
}

export default Events;